import { useSelector } from 'react-redux';

import {
	AutoplayData,
	CreateOrUpdateAutoplayTimer,
	DeleteAutoplayTimer,
	GetAutoplayTimer,
} from '../connection/autoplay';
import type { Shot } from '../stores/types/shots';
import { GreenroomState as GRS } from '../stores/types/types';
import * as Alerts from '../components/general-components/alert-service';
import { useVideoMachine } from '../contexts/video-machine';
import { AUTOPLAY_SECOND_BUFFER } from './use-shot-utilities';
import { GetScheduledStreamValidation } from '../connection/scheduled-streams';
import { ScheduledStreamValidation } from '../stores/types/connection';
import { getEffectiveShotDuration } from './use-effective-shot-duration';

interface AllShotsAutoplayResult {
	autoplayOn: boolean;
	affectedShots: Shot[];
	validationResults: ScheduledStreamValidation[];
}

interface UseAutoplayReturn {
	setShotAutoplay: (
		shot: Shot,
		durationLeft?: number,
	) => Promise<AutoplayData | null>;
	getShotAutoplay: (
		shot: Shot,
	) => Promise<AutoplayData | null>;
	updateShotAutoplay: (
		shot: Shot,
		timerDifference?: number,
	) => Promise<AutoplayData | null>;
	deleteShotAutoplay: (
		shot: Shot,
		checkIfAutoplay?: boolean,
	) => Promise<boolean>;
	getShotToggleAutoplay: (
		shot: Shot,
		canChangeTimer: boolean,
		shotStartTimestampEntity: Date | null,
		autoplayOn?: boolean,
		isScheduledStreamDoNotSetAutoplayIfNoDuration?: boolean,
		backEndValidationOnly?: boolean,
	) => Shot | null;
	getAllShotsChangeAutoplay: (
		shots: Shot[],
		autoplayOn: boolean,
		currentShotIdNumber: number,
		shotStartTimestampEntity: Date | null,
	) => Promise<AllShotsAutoplayResult>;
	remainingTimeInShot: (
		shotStartTimestampEntity: Date | null,
		shotDuration: number,
		autoDuration: boolean,
		videoMachineTimeLeft: number
	) => number | null
	showToasterAutoPlayRemoved: () => Promise<void>,
	getIsDurationInputDisabled: (shot: Shot) => [boolean, string]
}

/*
	This custom hook provides an easy way to use timer CRUD methods in the code.
	It automatically checks if the shot is autoplay before proceeding.
*/

const useAutoplay = (): UseAutoplayReturn => {
	const { videoMachineState } = useVideoMachine();

	const meetingId = useSelector((s: GRS) => s.SessionReducer.meeting?.meeting);
	const token = useSelector((s: GRS) => s.SessionReducer.token);
	const currentShotId = useSelector((s: GRS) => s.ShotsReducer.currentShotId);
	const reduxShotStartTimestamp = useSelector(
		(s: GRS) => s.ShotsReducer.shotStartTimestampEntity?.entityValue?.start,
	);
	const distributedShotDurations = useSelector((s: GRS) => s.SegmentsReducer.distributedShotDurations);
	const useSegments = useSelector((s: GRS) => s.SettingsReducer.featureFlags.use_segments);

	const setShotAutoplay = async (
		shot: Shot,
		durationLeft?: number,
	): Promise<AutoplayData | null> => {
		const {
			shot_id: shotId,
			shot_data: {
				autoplay,
				shotDuration,
			},
		} = shot;

		// don't use effectiveShotSuration because a Segment distributed Shot duration should 
		// already be applied after toggling autoplay on and this should verify that
		const durationToUse = durationLeft ?? shotDuration;

		if (
			!autoplay
			|| !token
			|| !shotId
			|| !durationToUse
			|| !meetingId
		) {
			return null;
		}

		const shotTimer = await CreateOrUpdateAutoplayTimer(
			token,
			shotId,
			Math.round(durationToUse),
			meetingId,
		);

		return shotTimer;
	};

	const getShotAutoplay = async (
		shot: Shot,
	): Promise<AutoplayData | null> => {
		if (!token || !shot?.shot_data?.autoplay || !meetingId) {
			return null;
		}

		const shotTimer = await GetAutoplayTimer(
			token,
			shot.shot_id,
			meetingId,
		);

		return shotTimer;
	};

	const deleteShotAutoplay = async (
		shot: Shot,
		checkIfAutoplay?: boolean,
	): Promise<boolean> => {
		if (
			!token
			|| (checkIfAutoplay && !shot?.shot_data?.autoplay)
			|| !meetingId
		) {
			return false;
		}

		const timerDeleted = await DeleteAutoplayTimer(
			token,
			shot.shot_id,
			meetingId,
		);

		return timerDeleted;
	};

	const updateShotAutoplay = async (
		shot: Shot,
		timerDifference?: number,
	): Promise<AutoplayData | null> => {
		const currentTimer = await getShotAutoplay(shot);

		if (!currentTimer) {
			return null;
		}

		const updatedTime = timerDifference
			? currentTimer.durationLeft + timerDifference
			: currentTimer.durationLeft;

		// Either update timer or destroy it if less than min time left
		if (updatedTime > 1) {
			const update = await setShotAutoplay(
				shot,
				updatedTime,
			);

			return update;
		}

		await deleteShotAutoplay(shot);

		return null;
	};

	const remainingTimeInShot = (
		shotStartTimestampEntity: Date | null,
		shotDuration: number,
		autoDuration: boolean,
		timeLeftInVideo: number,
	): number | null => {
		if (!shotStartTimestampEntity) return null;

		if (autoDuration) {
			return (timeLeftInVideo ?? AUTOPLAY_SECOND_BUFFER) - AUTOPLAY_SECOND_BUFFER;
		}

		if (shotDuration === 0) return null;

		const secondsPassed = (new Date().valueOf() - shotStartTimestampEntity.valueOf()) / 1000;
		const secondsRemaining = shotDuration - secondsPassed;
		return secondsRemaining;
	};

	const getShotToggleAutoplay = (
		shot: Shot,
		canChangeTimer: boolean,
		shotStartTimestampEntity: Date | null, // force timer to a number of seconds
		autoplayOn?: boolean,
		isScheduledStreamDoNotSetAutoplayIfNoDuration: boolean = false,
		backEndValidationOnly: boolean = false,
	): Shot | null => {
		if (!token || !shot) {
			return null;
		}

		const updatedShot: Shot = JSON.parse(JSON.stringify(shot));
		const newAutoplay = autoplayOn === undefined
			? !updatedShot.shot_data.autoplay
			: autoplayOn;

		const shotDuration = getEffectiveShotDuration(updatedShot, useSegments, distributedShotDurations);

		if (
			isScheduledStreamDoNotSetAutoplayIfNoDuration
			&& newAutoplay
			&& !shotDuration
			&& !updatedShot.shot_data.autoDuration
		) {
			return null;
		}

		if (
			!backEndValidationOnly
			&& !shot.shot_data.shotDuration
			&& !shot.shot_data.autoDuration
			&& newAutoplay
		) {
			// save computed duration as actual shotDuration or fall back to default 60s
			updatedShot.shot_data.shotDuration = shotDuration || 60;
		}

		updatedShot.shot_data.autoplay = newAutoplay;

		const { autoplay, autoDuration } = updatedShot.shot_data;

		// If can change, add timer if toggled on, remove timer if toggled off
		if (canChangeTimer) {
			if (autoplay && !autoDuration) {
				// shot was playing when autoplay was toggled on
				const secondsRemaining = remainingTimeInShot(
					shotStartTimestampEntity,
					shotDuration,
					autoDuration,
					videoMachineState.timeLeft,
				);
					// don't set autoplay timer for shots who's duration has played all the way through
				if (secondsRemaining !== null && secondsRemaining > AUTOPLAY_SECOND_BUFFER) {
					setShotAutoplay(updatedShot, secondsRemaining);
				}
			} else {
				deleteShotAutoplay(updatedShot);
			}
		}

		return updatedShot;
	};

	const getAllShotsChangeAutoplay = async (
		shots: Shot[],
		autoplayOn: boolean,
		currentShotIdNumber: number,
		shotStartTimestampEntity: Date | null,
	): Promise<AllShotsAutoplayResult> => {
		if (!token || !shots || !meetingId) {
			return {
				autoplayOn,
				affectedShots: [],
				validationResults: [],
			};
		}

		const affectedShots: Shot[] = [];
		const validationResults = await GetScheduledStreamValidation(token, meetingId);
		shots.forEach((shot, index) => {
			const canChangeTimer = !!shotStartTimestampEntity
				&& shot.shot_id === currentShotIdNumber;
			const shotAutoplayResult = getShotToggleAutoplay(
				shot,
				canChangeTimer,
				shotStartTimestampEntity,
				true,
				true,
			);

			if (shotAutoplayResult) {
				affectedShots.push(shotAutoplayResult);
			}
		});

		return {
			autoplayOn,
			affectedShots,
			validationResults,
		};
	};

	const showToasterAutoPlayRemoved = async () => {
		Alerts.showAlert({
			message: 'Autoplay Setting Disabled',
			description: 'Removing a manual duration or auto duration cancels Autoplay',
			type: 'neutral',
			duration: 5000,
		});
	};

	const getIsDurationInputDisabled = (shot: Shot): [boolean, string] => {
		const {
			shot_data: {
				autoplay: reduxAutoplay,
				autoDuration: reduxAutoDuration,
			},
			shot_id: shotId,
		} = shot;

		if (reduxAutoDuration // Shot is auto duration
			|| (!!reduxShotStartTimestamp // Currently playing shots
				&& reduxAutoplay // Shot is autoplay
				&& shotId === currentShotId // Shot is on canvas
			)
		) {
			const message = reduxAutoDuration
				? 'Auto Duration'
				: 'Cannot edit duration on played shot';

			return [true, message];
		}

		return [false, ''];
	};

	return {
		setShotAutoplay,
		getShotAutoplay,
		updateShotAutoplay,
		deleteShotAutoplay,
		getShotToggleAutoplay,
		getAllShotsChangeAutoplay,
		showToasterAutoPlayRemoved,
		remainingTimeInShot,
		getIsDurationInputDisabled,
	};
};

export default useAutoplay;
